/* eslint-disable camelcase */
import { pushRoute } from '../../../lib/router.js';
import { omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import {
  createStakeholderDraft,
  deleteStakeholder,
  finishUpdateStakeholderDraft,
  retrieveStakeholders,
  selectStakeholder,
  selectStakeholderDraft,
  selectStakeholderDraftId,
  updateStakeholderDraft,
} from '../../../modules/stakeholders.js';
import StepStakeholderForm from '../../../components/projects/Stakeholders/StakeholderForms/StepStakeholderForm.jsx';
import { retrieveProject, selectProject } from '../../../modules/projects.js';
import useConvertToBase64 from '../../../hooks/useConvertToBase64.js';
import {
  generalStepValidate,
  navigateToStepInformation,
  onCreateUpdateCancel,
  stakeholdersUrl,
  stakeholderUrl,
} from './utils/index.js';
import {
  retrieveOrgStakeholders,
  selectOrgStakeholders,
  selectOrgStakeholdersWithArchived,
  selectRetrieveOrgStakeholdersState,
} from '../../../modules/orgStakeholders.js';
import OrgStakeholderFormContainer from '../../organizations/OrgStakeholderFormContainer.jsx';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import StakeholderFormHeader from '../../../components/headers/PageHeaders/Project/Stakeholders/StakeholderFormHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save stakeholder');

const mapStateToProps = (state, ownProps) => ({
  editing: !!ownProps.stakeholderId,
  draftId: selectStakeholderDraftId(state),
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
  stakeholderId: ownProps.stakeholderId,
  stakeholder: omit(selectStakeholder(state, ownProps), [
    'id',
    'influencePosition',
    'interestPosition',
  ]),
  stakeholderDraft: selectStakeholderDraft(selectStakeholderDraftId(state))(
    state
  ),
  orgStakeholders: ownProps.stakeholderId
    ? selectOrgStakeholdersWithArchived(state)
    : selectOrgStakeholders(state),
  orgStakeholdersRetrieveSuccess:
    selectRetrieveOrgStakeholdersState(state).success,
});

const mapDispatchToProps = {
  deleteStakeholder,
  retrieveStakeholders,
  retrieveProject,
  createStakeholderDraft,
  updateStakeholderDraft,
  retrieveOrgStakeholders,
  finishUpdateStakeholderDraft,
};

function StepStakeholderFormContainer({
  slug,
  draftId,
  deleteStakeholder,
  projectId,
  project,
  retrieveProject,
  stakeholder,
  stakeholderId,
  stakeholderDraft,
  createStakeholderDraft,
  updateStakeholderDraft,
  orgStakeholders,
  retrieveOrgStakeholders,
  retrieveStakeholders,
  orgStakeholdersRetrieveSuccess,
  finishUpdateStakeholderDraft,
  editing,
}) {
  const { error, convertToBase64 } = useConvertToBase64();

  const [selectedImage, setSelectedImage] = useState('');
  const [imageSize, setImageSize] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);

  const [stakeholderParentType, setStakeholderParentType] =
    useState('existing');
  const [selectedOrgStakeholderId, setSelectedOrgStakeholderId] =
    useState(null);

  const initialValues = draftId ? stakeholderDraft : stakeholder;

  const goNext = () =>
    navigateToStepInformation(editing, projectId, stakeholderId, slug);

  const clearImageField = () => setSelectedImage('');

  const findParent = (arr, selectedId) =>
    arr.find(({ id }) => id === selectedId);

  const findField = (nativeName) =>
    project
      ? project.stakeholderFields.find(
          (field) => field.nativeColumn === nativeName
        )
      : [];

  const fields = {
    memberCount: findField('member_count'),
    influence: findField('influence'),
    interest: findField('interest'),
    impact: findField('impact'),
    engagementFrequency: findField('engagement_frequency'),
    supportForChange: findField('support'),
  };

  const onDelete = () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this stakeholder profile?'
      )
    )
      return;
    deleteStakeholder(stakeholderId).then(() =>
      pushRoute(stakeholdersUrl, { projectId, slug })
    );
  };

  const onOrgStakeholderChange = (id) => {
    setSelectedOrgStakeholderId(id);
    mapErrors({ errors: {}, message: '' });
  };

  const onCancel = () =>
    onCreateUpdateCancel(draftId, deleteStakeholder, projectId, slug);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    try {
      const result = await convertToBase64(file);
      setSelectedImage(result);
      setImageSize(result.length);
    } catch (err) {
      console.error(error);
    }
  };

  const onSubmit = (values) => {
    const { finish } = values;

    const stepValues = Object.values(fields)
      .map((field) => field.key)
      .reduce((acc, item) => {
        acc[item] = values[item] || null;
        return acc;
      }, {});

    const name =
      stakeholderParentType === 'existing'
        ? findParent(orgStakeholders, selectedOrgStakeholderId).fullName
        : values.name;

    const draft = {
      name,
      organization_stakeholder_id:
        stakeholderParentType === 'existing' ? selectedOrgStakeholderId : null,
      ...stepValues,
      avatar: selectedImage || null,
    };

    const submitPromise = finish
      ? finishUpdateStakeholderDraft(
          !draftId ? stakeholderId : draftId,
          draft,
          false,
          true
        )
      : !draftId
        ? createStakeholderDraft(projectId, stakeholderId, draft, editing)
        : updateStakeholderDraft(draftId, draft, false);

    setLoading(true);
    return submitPromise
      .then(({ result }) =>
        finish
          ? pushRoute(stakeholderUrl, { projectId, stakeholderId: result })
          : goNext()
      )
      .catch(mapErrors)
      .finally(() => setLoading(false));
  };

  const validate = (values) =>
    generalStepValidate(
      selectedOrgStakeholderId,
      stakeholderParentType,
      selectedImage,
      imageSize,
      values
    );

  const onAddOrgStakeholderSubmitSuccess = (id) => {
    setSelectedOrgStakeholderId(id);
    setStakeholderParentType('existing');
  };
  const setNameData = (subject) => {
    const { organization_stakeholder_id, name } = subject;

    const nameExist = orgStakeholders.find(
      (stakeholder) => stakeholder.fullName === name
    );

    if (organization_stakeholder_id && nameExist && !selectedOrgStakeholderId) {
      setSelectedOrgStakeholderId(organization_stakeholder_id);
      setStakeholderParentType('existing');
    } else {
      setStakeholderParentType('independent');
    }
  };

  useEffect(() => {
    retrieveStakeholders(projectId);
    retrieveProject(projectId);
    retrieveOrgStakeholders();
  }, []);

  useEffect(() => {
    if (
      Object.keys(stakeholder).length &&
      !selectedOrgStakeholderId &&
      orgStakeholders
    ) {
      setNameData(stakeholder);
    }
  }, [stakeholder]);

  useEffect(() => {
    if (stakeholderDraft && !selectedOrgStakeholderId && orgStakeholders) {
      setNameData(stakeholderDraft);
    }
  }, [stakeholderDraft]);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        onDelete={onDelete}
        editing={editing}
        subject="Stakeholder Profile"
        typeStep="Stakeholder"
      >
        {project && (editing ? Object.keys(stakeholder).length : true) ? (
          <StakeholderFormHeader
            slug={slug}
            project={project}
            editing={editing}
            stakeholder={stakeholder}
            stakeholderId={stakeholderId}
          />
        ) : null}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              {project && orgStakeholders ? (
                <Row>
                  <Col sm={10} smOffset={1}>
                    {orgStakeholdersRetrieveSuccess ? (
                      <Form
                        onSubmit={onSubmit}
                        initialValues={
                          editing ? initialValues : stakeholderDraft || []
                        }
                        validate={validate}
                        onCancel={onCancel}
                        editing={editing}
                        fields={fields}
                        loading={loading}
                        handleImageSelect={handleImageSelect}
                        clearImageField={clearImageField}
                        orgStakeholders={orgStakeholders}
                        selectedOrgStakeholderId={selectedOrgStakeholderId}
                        setSelectedOrgStakeholderId={onOrgStakeholderChange}
                        stakeholderParentType={stakeholderParentType}
                        setStakeholderParentType={setStakeholderParentType}
                        onShowAddStakeholderModal={() => setShowFormModal(true)}
                        stakeholder={stakeholder}
                        stakeholderDraft={stakeholderDraft}
                        slug={slug}
                        component={StepStakeholderForm}
                      />
                    ) : null}
                  </Col>
                </Row>
              ) : null}
            </div>
          </Col>
        </Row>
      </Grid>
      <OrgStakeholderFormContainer
        onHideFormModal={() => setShowFormModal(false)}
        showFormModal={showFormModal}
        onSubmitSuccess={onAddOrgStakeholderSubmitSuccess}
      />
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepStakeholderFormContainer);
